import * as React from 'react';
import {useSyncExternalStore, type ReactNode} from 'react';
import {buttonVariants} from '@/components/app/button';
import {dialogTitleClassName} from '@/components/app/modal';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {Icon, type IconProp} from '@/app/components/Icon';
import {cn} from '@/lib/utils';

export interface ConfirmationOptions {
  title?: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmClassName?: string;
  /** Use 'destructive' for irreversible actions such as deleting data. */
  confirmVariant?: React.ComponentProps<typeof AlertDialogAction>['variant'];
  cancelClassName?: string;
  className?: string;
  icon?: IconProp;
  confirmIcon?: IconProp;
  showCancel?: boolean;
  dismissible?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onShow?: () => void;
}
export interface ConfirmationHandle {
  close: () => void;
}
interface PendingConfirmation {
  id: number;
  options: ConfirmationOptions;
  resolve: (value: boolean) => void;
  origin: HTMLElement | null;
}
let nextId = 0;
let pending: PendingConfirmation[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
function settle(id: number, accepted: boolean, explicitCancel = false) {
  const entry = pending.find((item) => item.id === id);
  if (!entry) return;
  pending = pending.filter((item) => item.id !== id);
  emit();
  entry.resolve(accepted);
  if (accepted) entry.options.onConfirm?.();
  else if (explicitCancel) entry.options.onCancel?.();
}
export function requestConfirmation(
  options: ConfirmationOptions,
  callback?: (handle: ConfirmationHandle) => void,
): Promise<boolean> {
  const id = ++nextId;
  const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  return new Promise((resolve) => {
    pending = [...pending, {id, options, resolve, origin}];
    emit();
    callback?.({close: () => settle(id, false)});
  });
}
export function ConfirmationHost() {
  const entries = useSyncExternalStore(
    subscribe,
    () => pending,
    () => pending,
  );
  const entry = entries[0];
  if (!entry) return null;
  const {id, options} = entry;
  return (
    <AlertDialog
      key={id}
      open
      onOpenChange={(open) => {
        if (!open) settle(id, false);
      }}
    >
      <AlertDialogContent
        // PrimeReact spacing: padded header, message without top padding, then a compact footer.
        className={cn('gap-0 p-0', options.className)}
        onOpenAutoFocus={() => options.onShow?.()}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (entry.origin?.isConnected) entry.origin.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (options.dismissible === false) event.preventDefault();
        }}
      >
        <AlertDialogHeader className="place-items-stretch gap-0 text-left">
          <AlertDialogTitle className={cn(dialogTitleClassName, 'p-6')}>
            {options.title ?? 'Confirmation'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild className="flex items-center gap-4 px-6 pb-6 text-base text-foreground">
            <div>
              {options.icon && <Icon icon={options.icon} className="size-7 shrink-0 text-foreground" />}
              <div>{options.message}</div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="px-6 py-4">
          {options.showCancel !== false && (
            <AlertDialogCancel
              variant="ghost"
              className={cn(buttonVariants({variant: 'text'}), options.cancelClassName)}
              onClick={() => settle(id, false, true)}
            >
              {options.cancelLabel ?? 'No'}
            </AlertDialogCancel>
          )}
          <AlertDialogAction
            variant={options.confirmVariant}
            className={options.confirmClassName}
            onClick={() => settle(id, true)}
          >
            {options.confirmIcon && <Icon icon={options.confirmIcon} />} {options.confirmLabel ?? 'Yes'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export {requestConfirmation as confirm};
